
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';

interface DraggableItem {
  value: string;
  label: string;
  id?: string; // Stable ID for React keys
  color?: string; // Color for status badges
  isDefault?: boolean; // Whether this is a default item that cannot be deleted
}

interface DraggableListProps {
  items: DraggableItem[];
  onItemsChange: (items: DraggableItem[]) => void;
  onItemChange: (index: number, item: DraggableItem) => void;
  onItemDelete: (index: number) => void;
  onItemAdd: () => void;
  disabled?: boolean;
  placeholder?: string;
  addButtonText?: string;
  valueGenerator: (label: string) => string;
  canDeleteItem?: (item: DraggableItem) => boolean;
  getUsageCount?: (item: DraggableItem) => number;
  showColorPicker?: boolean;
}

interface SortableItemProps {
  item: DraggableItem;
  index: number;
  disabled?: boolean;
  placeholder?: string;
  onItemChange: (index: number, item: DraggableItem) => void;
  onItemDelete: (index: number) => void;
  valueGenerator: (label: string) => string;
  canDeleteItem?: (item: DraggableItem) => boolean;
  getUsageCount?: (item: DraggableItem) => number;
  showColorPicker?: boolean;
}

function SortableItem({ 
  item, 
  index, 
  disabled, 
  placeholder, 
  onItemChange, 
  onItemDelete, 
  valueGenerator,
  canDeleteItem,
  getUsageCount,
  showColorPicker = false
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id || item.value,
    disabled: disabled 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canDelete = canDeleteItem ? canDeleteItem(item) : true;
  const usageCount = getUsageCount ? getUsageCount(item) : 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 items-center p-2 rounded-md border border-border ${
        isDragging ? 'bg-accent/50 shadow-lg' : 'bg-card'
      }`}
    >
      <div
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        className={`p-1 rounded transition-colors ${
          disabled 
            ? 'opacity-50 cursor-not-allowed' 
            : 'cursor-grab active:cursor-grabbing hover:bg-accent hover:text-accent-foreground'
        }`}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      
      {showColorPicker && (
        <input
          type="color"
          value={item.color || '#6b7280'}
          onChange={(e) => onItemChange(index, { ...item, color: e.target.value })}
          disabled={disabled}
          className="w-8 h-8 rounded border border-border cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          title="Choose color"
        />
      )}
      
      <Input
        className="flex-1"
        placeholder={placeholder}
        disabled={disabled || item.isDefault}
        value={item.label}
        onChange={(e) => {
          const label = e.target.value;
          onItemChange(index, { 
            ...item,
            label,
            value: valueGenerator(label)
          });
        }}
      />
      
      <Button
        variant="destructive"
        size="sm"
        disabled={disabled || !canDelete}
        onClick={() => onItemDelete(index)}
        title={
          item.isDefault 
            ? "Default items cannot be deleted"
            : usageCount > 0 
            ? `Cannot delete: ${usageCount} item(s) are using this status`
            : "Delete item"
        }
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </Button>
    </div>
  );
}

export function DraggableList({
  items,
  onItemsChange,
  onItemChange,
  onItemDelete,
  onItemAdd,
  disabled = false,
  placeholder = "Enter item name",
  addButtonText = "Add Item",
  valueGenerator,
  canDeleteItem,
  getUsageCount,
  showColorPicker = false,
}: DraggableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

     function handleDragEnd(event: DragEndEvent) {
     const { active, over } = event;

     if (active.id !== over?.id) {
       const oldIndex = items.findIndex(item => (item.id || item.value) === active.id);
       const newIndex = items.findIndex(item => (item.id || item.value) === over?.id);

       onItemsChange(arrayMove(items, oldIndex, newIndex));
     }
   }

  return (
    <div className="space-y-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
                 <SortableContext
           items={items.map(item => item.id || item.value)}
           strategy={verticalListSortingStrategy}
         >
           <div className="space-y-2">
             {items.map((item, index) => (
               <SortableItem
                 key={item.id || item.value}
                item={item}
                index={index}
                disabled={disabled}
                placeholder={placeholder}
                onItemChange={onItemChange}
                onItemDelete={onItemDelete}
                valueGenerator={valueGenerator}
                canDeleteItem={canDeleteItem}
                getUsageCount={getUsageCount}
                showColorPicker={showColorPicker}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={onItemAdd}
      >
        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        {addButtonText}
      </Button>
    </div>
  );
}
